import React, { useEffect, useState } from "react";
import "../assets/AllTabsCommon.css";
import { fetchData, postData } from "../api/restapi";
import Select from "react-select"; // Import Select component
import DeviceTimeline from "./DeviceTimeline"; // Import DeviceTimeline component

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    borderColor: 'rgb(103, 114, 132)',
  }),
  valueContainer: (base) => ({
    ...base,
    height: '38px',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '38px',
  }),
  placeholder: (base) => ({
    ...base,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    color: state.isSelected ? 'white' : 'rgba(0, 0, 0, 0.85)',
    padding: '8px 12px',
    backgroundColor: state.isSelected
      ? 'rgb(50, 83, 220)'
      : state.isFocused
      ? '#f0f4ff'
      : 'white',
  }),
};

function DeviceDetailsTab({ setSelectedTab, setPrefilledSystemId, userInfo, role }) {
  const [connectedClients, setConnectedClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const rowsPerPage = 10; // Configurable value
  const [sortOrder, setSortOrder] = useState("asc");
  const [isRefreshing, setIsRefreshing] = useState(false); // New state for refresh loading

  const [expandedRowId, setExpandedRowId] = useState(null);
  const [recoveryInProgressSystemId, setRecoveryInProgressSystemId] = useState(null);
  const [isRemoteSessionActive, setIsRemoteSessionActive] = useState({}); // New state for remote session status
  const [showStatusSortDropdown, setShowStatusSortDropdown] = useState(false); // New state for dropdown visibility
  const [showTimelineModal, setShowTimelineModal] = useState(false); // State for timeline modal
  const [timelineSystemId, setTimelineSystemId] = useState(""); // System ID for timeline modal

  // State variables for AddDeviceRecovery functionality
  const [recoveryCurrentStep, setRecoveryCurrentStep] = useState(1);
  const [recoverySystemId, setRecoverySystemId] = useState("");
  const [modemSsrCommand, setModemSsrCommand] = useState(null);
  const [recoverySuccessMessage, setRecoverySuccessMessage] = useState("");
  const [recoveryErrorMessage, setRecoveryErrorMessage] = useState("");
  const [recoveryValidationError, setRecoveryValidationError] = useState("");

  const commandOptions = [
    { value: "MODEM_SSR", label: "MODEM_SSR" },
    { value: "DEVICE_RESET", label: "DEVICE_RESET" },
  ];

  const visibleColumns = [
    { key: "systemId", label: "System ID" },
    { key: "deviceTag", label: "Device Tag" },
    { key: "chipset", label: "Chipset" },
    { key: "oem", label: "OEM" },
    { key: "clientStatus", label: "Status" },
    { key: "timeline", label: "Timeline", isAction: true },
    { key: "remoteSession", label: "Remote Diagnostic", isAction: true },
    { key: "rtdmSession", label: "Real-Time Device Mgmt", isAction: true },
    { key: "deviceRecovery", label: "Device Recovery", isAction: true }
  ];

  const hiddenColumns = [
    { key: "make", label: "Make" },
    { key: "model", label: "Model" },
    { key: "mpssBuild", label: "MPSS Build" },
    { key: "leBuild", label: "LE Build" },
    { key: "product", label: "Product" },
    { key: "uptimeDevice", label: "Device Uptime (seconds)" },
    { key: "uptimeModem", label: "Modem Uptime (seconds)" },
    { key: "uptimeAp", label: "AP Uptime (seconds)" },
    { key: "updated_on", label: "Status Last Changed" },
    { key: "lastMqttConnectionDurationSec", label: "Last MQTT Connection Duration (in secs)" },
  ];

  const tableColumns = visibleColumns; // This is redundant, but kept for consistency

  useEffect(() => {
    const fetchConnectedClients = async () => {
      try {
        const data = await fetchData(`/api/v1/devices?userRole=Admin&userEmailId=${userInfo}`);
        if (data.statusCode === 200) {
          setConnectedClients(data.registeredClients);
          setFilteredClients(data.registeredClients);
        }
      } catch (err) {
        // Handle error silently
      }
    };
    fetchConnectedClients();
  }, [userInfo, role]);

  // New useEffect to fetch remote session status
  useEffect(() => {
    const fetchRemoteSessionStatus = async () => {
      const newRemoteSessionStatus = {};
      const connectedSystemIds = connectedClients
        .filter(client => client.clientStatus === "CONNECTED")
        .map(client => client.systemId);

      for (const systemId of connectedSystemIds) {
        try {
          const data = await fetchData(`/api/v1/rd-session-status/${systemId}`);
          if (data.statusCode === 200 && data.isActiveRDSession) {
            newRemoteSessionStatus[systemId] = true;
          } else {
            newRemoteSessionStatus[systemId] = false;
          }
        } catch (err) {
          newRemoteSessionStatus[systemId] = false; // Assume not active on error
        }
      }
      setIsRemoteSessionActive(prevStatus => ({ ...prevStatus, ...newRemoteSessionStatus }));
    };

    if (connectedClients.length > 0) {
      fetchRemoteSessionStatus();
    }
  }, [connectedClients]);

  // Effect to handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the dropdown container
      // You might need to add a ref to the th element or the dropdown container
      // For simplicity, we'll just close it if any click happens outside the header
      // A more robust solution would involve refs to specific elements.
      if (showStatusSortDropdown && !event.target.closest('.sort-dropdown-container') && !event.target.closest('.sortable-header')) {
        setShowStatusSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusSortDropdown]);

  const handleSort = (direction) => {
    const sortedClients = [...filteredClients].sort((a, b) => {
      if (direction === "asc") {
        return a.clientStatus.localeCompare(b.clientStatus);
      } else {
        return b.clientStatus.localeCompare(a.clientStatus);
      }
    });
    setFilteredClients(sortedClients);
    setSortOrder(direction); // Update sortOrder to reflect the applied direction
  };

  const handleSortAscending = () => {
    handleSort("asc");
    setShowStatusSortDropdown(false); // Close dropdown after sorting
  };

  const handleSortDescending = () => {
    handleSort("desc");
    setShowStatusSortDropdown(false); // Close dropdown after sorting
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    if (term === "") {
      setFilteredClients(connectedClients);
    } else {
      const filtered = connectedClients.filter(client =>
        client.systemId.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredClients(filtered);
    }
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleStartRemoteSession = (systemId) => {
    // Pre-populate the system ID and indicate the desired sub-tab for Start Remote Debug tab
    if (setPrefilledSystemId) {
      setPrefilledSystemId({ systemId: systemId, subTab: 'Start' });
    }
    // Navigate to Remote Diagnostics main tab (not sub-tab)
    if (setSelectedTab) {
      setSelectedTab("Diagnostics");
    }
  };

  const handleStartRTDMSession = (systemId) => {
    if (setPrefilledSystemId) {
      setPrefilledSystemId({ systemId: systemId, subTab: 'RTDMStart' });
    }
    if (setSelectedTab) {
      setSelectedTab("Diagnostics");
    }
  };

  const handleUpdateEndRemoteSession = (systemId, actionType) => {
    // Pre-populate the system ID and indicate the desired sub-tab for End Remote Debug tab
    // Both 'Update' and 'End' actions lead to the 'Active' sub-tab of Remote Diagnostics
    if (setPrefilledSystemId) {
      setPrefilledSystemId({ systemId: systemId, subTab: 'Active' });
    }
    if (setSelectedTab) {
      setSelectedTab("Diagnostics");
    }
    // The `actionType` could be used if there were different UIs/flows for Update vs End on the target tab
    // For now, both lead to the 'Active' sub-tab with the systemId pre-filled.
  };

  const handleDeviceRecovery = (systemId) => {
    setRecoveryInProgressSystemId(recoveryInProgressSystemId === systemId ? null : systemId);
    setRecoverySystemId(systemId); // Set the system ID for the inline form
    setRecoveryCurrentStep(1); // Reset to step 1 when opening/closing
    setModemSsrCommand(null); // Clear previous command selection
    setRecoverySuccessMessage(""); // Clear messages
    setRecoveryErrorMessage("");
    setRecoveryValidationError("");
  };

  const handleViewDetails = (systemId) => {
    setExpandedRowId(expandedRowId === systemId ? null : systemId);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Fetch device data
      const data = await fetchData(`/api/v1/devices?userRole=Admin&userEmailId=${userInfo}`);
      if (data.statusCode === 200) {
        setConnectedClients(data.registeredClients);
        
        // Apply current search filter to new data
        if (searchTerm === "") {
          setFilteredClients(data.registeredClients);
        } else {
          const filtered = data.registeredClients.filter(client =>
            client.systemId.toLowerCase().includes(searchTerm.toLowerCase())
          );
          setFilteredClients(filtered);
        }

        // Fetch remote session status for connected devices
        const newRemoteSessionStatus = {};
        const connectedSystemIds = data.registeredClients
          .filter(client => client.clientStatus === "CONNECTED")
          .map(client => client.systemId);

        for (const systemId of connectedSystemIds) {
          try {
            const sessionData = await fetchData(`/api/v1/rd-session-status/${systemId}`);
            if (sessionData.statusCode === 200 && sessionData.isActiveRDSession) {
              newRemoteSessionStatus[systemId] = true;
            } else {
              newRemoteSessionStatus[systemId] = false;
            }
          } catch (err) {
            newRemoteSessionStatus[systemId] = false;
          }
        }
        setIsRemoteSessionActive(newRemoteSessionStatus);
      }
    } catch (err) {
      // Handle error silently
    } finally {
      setIsRefreshing(false);
    }
  };

  const totalPages = Math.ceil(filteredClients.length / rowsPerPage);
  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const renderPageNumbers = () => {
    const maxPagesToShow = 6;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={currentPage === i ? "active" : ""}
        >
          {i}
        </button>
      );
    }

    return (
      <>
        <button onClick={() => handlePageChange(1)}>&lt;&lt;</button>
        {pageNumbers}
        <button onClick={() => handlePageChange(totalPages)}>&gt;&gt;</button>
      </>
    );
  };

  return (
    <div className="data-table-tab-container">
      <div className="search-container">
        <input
          type="text"
          placeholder="🔍 Search by System ID..."
          value={searchTerm}
          onChange={handleSearch}
        />
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{
            backgroundColor: isRefreshing ? '#6c757d' : '#007bff',
            color: 'white',
            padding: '0',
            border: 'none',
            borderRadius: '50%',
            cursor: isRefreshing ? 'not-allowed' : 'pointer',
            fontSize: '20px',
            fontWeight: '500',
            transition: 'background-color 0.3s ease',
            width: '42px',
            height: '42px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
          }}
          onMouseEnter={(e) => {
            if (!isRefreshing) e.target.style.backgroundColor = '#0056b3';
          }}
          onMouseLeave={(e) => {
            if (!isRefreshing) e.target.style.backgroundColor = '#007bff';
          }}
        >
          ↻
        </button>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th key={col.key} className={col.key === "clientStatus" ? "sortable-header" : ""}>
                  {col.key === "clientStatus" ? (
                    <>
                      <span>{col.label}</span>
                      <div className="sort-dropdown-container">
                        <span className="three-dots-icon" onClick={() => setShowStatusSortDropdown(!showStatusSortDropdown)}>
                          &#8942; {/* Vertical ellipsis character */}
                        </span>
                        {showStatusSortDropdown && (
                          <div className="sort-dropdown-menu">
                            <button onClick={handleSortAscending}>↑ Sort Ascending</button>
                            <button onClick={handleSortDescending}>↓ Sort Descending</button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedClients.map((client) => (
              <React.Fragment key={client.systemId}>
                <tr className={expandedRowId === client.systemId ? 'expanded-row' : ''}>
                  {visibleColumns.map((col) => {
                    if (col.isAction) {
                      if (col.key === "timeline") {
                        return (
                          <td key={col.key}>
                            <button
                              className="add-criteria-button"
                              onClick={() => {
                                setTimelineSystemId(client.systemId);
                                setShowTimelineModal(true);
                              }}
                            >
                              View
                            </button>
                          </td>
                        );
                      } else if (col.key === "remoteSession") {
                        const isInRemoteSession = isRemoteSessionActive[client.systemId];
                        return (
                          <td key={col.key}>
                            {client.clientStatus === "CONNECTED" ? (
                              isInRemoteSession ? (
                                <>
                                  <button
                                    className="add-criteria-button"
                                    onClick={() => {
                                      if (setPrefilledSystemId) {
                                        setPrefilledSystemId({ systemId: client.systemId, subTab: 'Active' });
                                      }
                                      if (setSelectedTab) {
                                        setSelectedTab("Diagnostics");
                                      }
                                    }}
                                  >
                                    Update
                                  </button>
                                  <button
                                    className="add-criteria-button"
                                    onClick={() => {
                                      if (setPrefilledSystemId) {
                                        setPrefilledSystemId({ systemId: client.systemId, subTab: 'Active' });
                                      }
                                      if (setSelectedTab) {
                                        setSelectedTab("Diagnostics");
                                      }
                                    }}
                                  >
                                    End
                                  </button>
                                </>
                              ) : (
                                  <button
                                    className="add-criteria-button"
                                    onClick={() => {
                                      handleStartRemoteSession(client.systemId);
                                    }}
                                  >
                                    Start
                                  </button>
                              )
                            ) : (
                              <span className="action-column-text">Offline</span>
                            )}
                          </td>
                        );
                      } else if (col.key === "rtdmSession") {
                        return (
                          <td key={col.key}>
                            {client.clientStatus === "CONNECTED" ? (
                              <button
                                className="add-criteria-button"
                                onClick={() => handleStartRTDMSession(client.systemId)}
                              >
                                Start
                              </button>
                            ) : (
                              <span className="action-column-text">Offline</span>
                            )}
                          </td>
                        );
                      } else if (col.key === "deviceRecovery") {
                        return (
                          <td key={col.key}>
                            {client.clientStatus === "CONNECTED" ? (
                              <button
                                className="add-criteria-button"
                                onClick={() => handleDeviceRecovery(client.systemId)}
                              >
                                {recoveryInProgressSystemId === client.systemId ? 'Hide Recovery' : 'Start'}
                              </button>
                            ) : (
                              <span className="action-column-text">Offline</span>
                            )}
                          </td>
                        );
                      }
                    }

                    let cellValue = client[col.key] || "";

                    if (col.key === "systemId") {
                      return (
                        <td key={col.key} onClick={() => handleViewDetails(client.systemId)} className="clickable-system-id">
                          {cellValue}
                        </td>
                      );
                    }
                    
                    // Format the updated_on field to display in browser local time
                    if (col.key === "updated_on" && cellValue) {
                      const date = new Date(cellValue);
                      cellValue = date.toLocaleString(undefined, {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: false
                      }).replace(",", "");
                    }
                    
                    // Add status color coding
                    if (col.key === "clientStatus") {
                      const statusClass = cellValue === "CONNECTED" ? "status-connected" : "status-offline";
                      return (
                        <td key={col.key}>
                          <span className={statusClass}>{cellValue}</span>
                        </td>
                      );
                    }
                    
                    return <td key={col.key}>{cellValue}</td>;
                  })}
                </tr>
                {expandedRowId === client.systemId && (
                  <tr className="expanded-details-row">
                    <td colSpan={visibleColumns.length}>
                      <div className="expanded-row-content">
                        <div className="expanded-details-cards">
                          <div className="expanded-detail-card">
                            <h4>Product Information</h4>
                            <div className="expanded-detail-card-content">
                              {hiddenColumns.filter(col => ["make", "model", "mpssBuild", "leBuild", "product"].includes(col.key)).map((col) => (
                                <div key={col.key} className="expanded-detail-item">
                                  <strong>{col.label}:</strong> {client[col.key] || "N/A"}
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div className="expanded-detail-card">
                            <h4>Connectivity Information</h4>
                            <div className="expanded-detail-card-content">
{hiddenColumns.filter(col => ["uptimeDevice", "uptimeModem", "uptimeAp", "updated_on", "lastMqttConnectionDurationSec"].includes(col.key)).map((col) => {
                                let cellValue = client[col.key] || "";
                                if (col.key === "updated_on" && cellValue) {
                                  const date = new Date(cellValue);
                                  cellValue = date.toLocaleString(undefined, {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: false
                                  }).replace(",", "");
                                }
                                return (
                                  <div key={col.key} className="expanded-detail-item">
                                    <strong>{col.label}:</strong> {cellValue || "N/A"}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {recoveryInProgressSystemId === client.systemId && (
                  <tr className="expanded-details-row">
                    <td colSpan={visibleColumns.length}>
                      <div className="content-tab-container">
                        <div className="tab-heading-inline">
                          <h1>Recover Device for {client.systemId}</h1>
                        </div>
                        
                        {recoveryValidationError && (
                          <div className="error-banner">
                            <div className="error-message">{recoveryValidationError}</div>
                          </div>
                        )}
                        
                        {recoveryCurrentStep === 1 && (
                          <>
                            <div className="row-inline">
                              <div className="dropdown-section">
                                <div className="dropdown-heading">
                                  System ID<span className="required-asterisk">*</span>
                                </div>
                                <input
                                  type="text"
                                  className="client-id"
                                  value={recoverySystemId}
                                  readOnly
                                />
                              </div>
                              <div className="dropdown-section">
                                <div className="dropdown-heading">
                                  Command<span className="required-asterisk">*</span>
                                </div>
                                <Select
                                  className="dropdown multi-select"
                                  styles={selectStyles}
                                  options={commandOptions}
                                  value={modemSsrCommand}
                                  onChange={(value) => setModemSsrCommand(value)}
                                  placeholder="Select Command"
                                />
                              </div>
                            </div>
                            <div className="submit-button-section">
                              <button
                                className="submit-button"
                                onClick={() => {
                                  if (!recoverySystemId.trim()) {
                                    setRecoveryValidationError("System ID is required");
                                    return;
                                  }
                                  if (!modemSsrCommand) {
                                    setRecoveryValidationError("Please select a command");
                                    return;
                                  }
                                  setRecoveryCurrentStep(2);
                                }}
                                disabled={!recoverySystemId.trim() || !modemSsrCommand}
                              >
                                Continue
                              </button>
                            </div>
                          </>
                        )}

                        {recoveryCurrentStep === 2 && (
                          <>
                            <div className="review-section">
                              <h3>Review your selection:</h3>
                              <div className="review-cards">
                                <div className="review-card">
                                  <h4>Device Recovery Request</h4>
                                  <div className="review-card-content">
                                    <div className="review-item">
                                      <span className="review-label">System ID:</span>
                                      <span className="review-value">{recoverySystemId}</span>
                                    </div>
                                    <div className="review-item">
                                      <span className="review-label">Command:</span>
                                      <span className="review-value">{modemSsrCommand?.label}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {recoverySuccessMessage && (
                              <div className="success-banner">
                                <div className="success-message">{recoverySuccessMessage}</div>
                                <button className="submit-button" onClick={() => {
                                  setRecoveryCurrentStep(1);
                                  setRecoverySuccessMessage("");
                                  setRecoveryErrorMessage("");
                                  setRecoveryValidationError("");
                                  setModemSsrCommand(null);
                                  setRecoveryInProgressSystemId(null); // Close the form
                                }}>
                                  Start New Request
                                </button>
                              </div>
                            )}
                            
                            {recoveryErrorMessage && (
                              <div className="error-banner">
                                <div className="error-message">{recoveryErrorMessage}</div>
                              </div>
                            )}
                            
                            {!recoverySuccessMessage && !recoveryErrorMessage && (
                              <div className="review-actions">
                                <button className="go-back-button" onClick={() => {
                                  setRecoveryCurrentStep(1);
                                  setRecoverySuccessMessage("");
                                  setRecoveryErrorMessage("");
                                }}>
                                  &lt; Go Back
                                </button>
                                <button className="submit-button" onClick={async () => {
                                  setRecoveryErrorMessage("");
                                  setRecoverySuccessMessage("");
                                  
                                  const requestBody = {
                                    systemId: recoverySystemId,
                                    modemSsrCommand: modemSsrCommand.value,
                                    user: userInfo || "admin", // Use userInfo prop if available, else default to "admin"
                                  };
                                  
                                  try {
                                    const response = await postData("/api/v1/device-recovery", requestBody); // Changed to postData
                                    if (response.statusCode === 200) {
                                      setRecoverySuccessMessage("Request sent successfully!");
                                    } else {
                                      setRecoveryErrorMessage(response.errorMessage || "Failed to send request.");
                                    }
                                  } catch (error) {
                                    setRecoveryErrorMessage("Failed to send request.");
                                  }
                                }}>
                                  Submit
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination-controls">{renderPageNumbers()}</div>
      
      {/* Timeline Modal */}
      {showTimelineModal && (
        <div className="modal-overlay" onClick={() => setShowTimelineModal(false)}>
          <div className="modal-content timeline-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Device Timeline - {timelineSystemId}</h2>
              <button className="modal-close-button" onClick={() => setShowTimelineModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <DeviceTimeline prefilledSystemId={timelineSystemId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceDetailsTab;
